import {all, call, put, takeEvery, takeLatest} from '@redux-saga/core/effects';
import {push} from 'connected-react-router';
import {AsyncResult, Loading, fromResult} from 'Core/models/Async';
import {createValidatedEmail, ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {createValidatedNonEmptyString} from 'Core/models/ValidatableNonEmptyString';
import {createValidatedPhone, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {
  changeUser,
  EnrollTrackUserActionType,
  getUser,
  save,
  setEmail,
  setPhone,
  setUsername,
  loadRoles,
  getRoles,
} from 'Enrollment/ducks/enrollTrackUser';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import * as TE from 'fp-ts/lib/TaskEither';
import {getApiCallGenerator, getValidationSaga} from 'Shared/utilities/sagaApiGenerators';
import userApi from 'Users/api/userApi';
import {ValidEnrollTrackUser} from 'Users/models/EnrollTrackUser';
import {UserRouteConstants} from 'Users/Routes';
import {RoleBase} from 'Users/api';
import roleApi from 'Users/api/roleApi';
import {createSnackbar} from 'Core/ducks/snackbar';
import {eq} from 'Shared/utilities/equals';
import {EnrollTrackRole} from 'Users/models/User/EnrollmentTrackingUserType';

export function* getRolesSaga() {
  yield takeLatest(EnrollTrackUserActionType.GET_ROLES, function*(action: ReturnType<typeof getRoles>) {
    const result: AsyncResult<RoleBase[]> = yield call(roleApi.getRoles);
    if (E.isRight(result)) {
      yield put(loadRoles(fromResult(result.right)));
    } else {
      yield put(createSnackbar('Unable to retrieve scopes'));
    }
  });
}

export function* validateEmailSaga() {
  yield takeLatest(
    EnrollTrackUserActionType.VALIDATE_EMAIL,
    getValidationSaga(ValidEmail.create, setEmail, userApi.validateEmail)
  );
}

export function* validatePhoneSaga() {
  yield takeLatest(
    EnrollTrackUserActionType.VALIDATE_PHONE,
    getValidationSaga(ValidPhoneNumber.create, setPhone, userApi.validatePhoneNumber)
  );
}

export function* validateUsernameSaga() {
  yield takeLatest(
    EnrollTrackUserActionType.VALIDATE_USERNAME,
    getValidationSaga(NonEmptyString.create, setUsername, userApi.validateAllstateUsername)
  );
}

const getEnrollTrackUserApi = (action: ReturnType<typeof getUser>) =>
  pipe(
    userApi.fetchEnrollTrackUser(action.payload.userId),
    TE.map(user =>
      ValidEnrollTrackUser.create(
        NonEmptyString.create(user.userName),
        NonEmptyString.create(user.lastName),
        createValidatedNonEmptyString(user.userName),
        createValidatedPhone(user.mobilePhone!),
        createValidatedEmail(user.email),
        pipe(
          () => user.roles.some(eq('id', EnrollTrackRole.UserSupport)),
          () => false
        ),
        user.roles.map(role => role.id!.toString())
      )
    )
  )();

function* getEnrollTrackUserSaga() {
  yield takeEvery(EnrollTrackUserActionType.GET_USER, getApiCallGenerator(getEnrollTrackUserApi, changeUser));
}

function* saveEnrollTrackUserApi(action: ReturnType<typeof save>) {
  yield put(changeUser(new Loading()));

  const result: AsyncResult<unknown> = yield call(
    userApi.saveEnrollTrackUser,
    action.payload.user,
    action.payload.userId
  );

  if (E.isRight(result)) {
    yield put(push(UserRouteConstants.USERS));
  }
}

function* saveEnrollTrackUserSaga() {
  yield takeLatest(EnrollTrackUserActionType.SAVE_USER, saveEnrollTrackUserApi);
}

export default function*() {
  return yield all([
    getRolesSaga(),
    getEnrollTrackUserSaga(),
    validateEmailSaga(),
    validatePhoneSaga(),
    validateUsernameSaga(),
    saveEnrollTrackUserSaga(),
  ]);
}
